/**
 * Copied from https://github.com/Kichiyaki/react-native-barcode-generator
 *
 * Other modules weren't cooperating so have copy pasted from this project. It's just 1 file.
 *
 * Have tested generated barcodes with barcode reader and it works.
 */
import React, {useMemo} from 'react';
import {View} from 'react-native';
import {TextStyle, ViewStyle} from 'react-native';
import Svg, {Path} from 'react-native-svg';
import barcodes from 'jsbarcode/src/barcodes';
import PropTypes from 'prop-types';

import {logger, serializeError} from '../../../utils/logger';
import Text from '../../ui-controls/Text';

export type Format =
  | 'CODE39'
  | 'CODE128'
  | 'CODE128A'
  | 'CODE128B'
  | 'CODE128C'
  | 'EAN13'
  | 'EAN8'
  | 'EAN5'
  | 'EAN2'
  | 'UPC'
  | 'UPCE'
  | 'ITF14'
  | 'ITF'
  | 'MSI'
  | 'MSI10'
  | 'MSI11'
  | 'MSI1010'
  | 'MSI1110'
  | 'pharmacode'
  | 'codabar';

export interface BarcodeProps {
  value: string;
  width?: number;
  maxWidth?: number;
  height?: number;
  format?: Format;
  lineColor?: string;
  background?: string;
  text?: React.ReactNode;
  textStyle?: TextStyle;
  style?: ViewStyle;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (err: any) => void;
}

const Barcode = ({
  value = '',
  width = 2,
  height = 100,
  format = 'CODE128',
  lineColor = '#000000',
  background = '#ffffff',
  text,
  textStyle,
  style,
  onError,
  maxWidth,
}: BarcodeProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const drawRect = (x: any, y: any, width: any, height: any) => {
    return `M${x},${y}h${width}v${height}h-${width}z`;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const drawSvgBarCode = (encoded: any) => {
    const rects = [];
    const {data: binary} = encoded;

    const barCodeWidth = binary.length * width;
    const singleBarWidth =
      typeof maxWidth === 'number' && barCodeWidth > maxWidth ? maxWidth / binary.length : width;
    let barWidth = 0;
    let x = 0;
    const yFrom = 0;

    for (let b = 0; b < binary.length; b++) {
      x = b * singleBarWidth;
      if (binary[b] === '1') {
        barWidth++;
      } else if (barWidth > 0) {
        rects[rects.length] = drawRect(
          x - singleBarWidth * barWidth,
          yFrom,
          singleBarWidth * barWidth,
          height,
        );
        barWidth = 0;
      }
    }

    if (barWidth > 0) {
      rects[rects.length] = drawRect(
        x - singleBarWidth * (barWidth - 1),
        yFrom,
        singleBarWidth * barWidth,
        height,
      );
    }

    return rects;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const encode = (text: any, Encoder: any) => {
    if (typeof text !== 'string' || text.length === 0) {
      throw new Error('Barcode value must be a non-empty string');
    }
    const encoder = new Encoder(text, {
      width,
      format,
      height,
      lineColor,
      background,
      flat: true,
    });
    if (!encoder.valid()) {
      throw new Error('Invalid barcode for selected format.');
    }
    return encoder.encode();
  };

  const {bars, barCodeWidth} = useMemo(() => {
    try {
      const encoder = barcodes[format];
      if (!encoder) {
        throw new Error('Invalid barcode format.');
      }
      const encoded = encode(value, encoder);
      const barCodeWidth = encoded.data.length * width;
      return {
        bars: drawSvgBarCode(encoded),
        barCodeWidth:
          typeof maxWidth === 'number' && barCodeWidth > maxWidth ? maxWidth : barCodeWidth,
      };
    } catch (error) {
      if (__DEV__) {
        logger.error('Barcode', 'Failed to generate barcode', serializeError(error));
      }
      if (onError) {
        onError(error);
      }
    }
    return {
      bars: [],
      barCodeWidth: 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, width, height, format, lineColor, background, maxWidth]);

  return (
    <View style={[{backgroundColor: background, alignItems: 'center'}, style]}>
      <Svg height={height} width={barCodeWidth} fill={lineColor}>
        <Path d={bars.join(' ')} />
      </Svg>
      {text ? <Text style={[{textAlign: 'center'}, textStyle]}>{text}</Text> : null}
    </View>
  );
};

Barcode.propTypes = {
  value: PropTypes.string.isRequired,
  format: PropTypes.oneOf(Object.keys(barcodes)),
  width: PropTypes.number,
  maxWidth: PropTypes.number,
  height: PropTypes.number,
  lineColor: PropTypes.string,
  background: PropTypes.string,
  text: PropTypes.node,
  textStyle: PropTypes.object,
  style: PropTypes.object,
  onError: PropTypes.func,
};

export default Barcode;
